import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Badge from "../../../components/ui-components/Badge";
import { secondaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import { formatPeriodLabel } from "../document/formatPeriod.js";

const RUN_STATUS_LABEL = {
  draft: "Draft",
  in_review: "In review",
  approved: "Approved",
  sent: "Sent",
  archived: "Archived",
};
const RUN_STATUS_TONE_KEY = {
  draft: "pending",
  in_review: "awaiting",
  approved: "approved",
  sent: "info",
  archived: "neutral",
};

function formatTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const Field = ({ label, children }) => (
  <div>
    <dt className="text-[11px] font-medium uppercase tracking-wide text-text-3">{label}</dt>
    <dd className="mt-0.5 text-sm text-text">{children}</dd>
  </div>
);
Field.propTypes = { label: PropTypes.string, children: PropTypes.node };

/**
 * OverviewPanel - the report home's landing tab: the status-at-a-glance the list can't show (latest
 * run + its workflow status, delivery), and the next steps (review the latest, edit content, manage
 * delivery). The primary "New run" action lives in the page header (BoardReportHome). Presentational:
 * the home owns report + run data.
 */
const OverviewPanel = ({ id, report, runs, isLoading, onManageDelivery }) => {
  const navigate = useNavigate();
  const list = Array.isArray(runs) ? runs : [];
  const latest = list[0];
  const recipients = Array.isArray(report?.recipients) ? report.recipients : [];

  return (
    <div className="max-w-3xl space-y-6">
      {/* Latest run */}
      <div className="bg-card border border-card-bd rounded-card p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-text">Latest run</h3>
          {latest && (
            <Badge
              status={RUN_STATUS_TONE_KEY[latest.status] || latest.status}
              label={RUN_STATUS_LABEL[latest.status] || latest.status}
            />
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-text-3">Loading…</p>
        ) : latest ? (
          <>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Version">v{latest.version}</Field>
              <Field label="Period">{formatPeriodLabel(latest.period)}</Field>
              <Field label="Created">{formatTimestamp(latest.created_at)}</Field>
            </dl>
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <button
                type="button"
                className={secondaryButtonCls}
                onClick={() => navigate(`/board-reports/${id}/runs/${latest.id}/review`)}
              >
                Review
              </button>
              <button
                type="button"
                className="text-sm text-primary hover:underline"
                onClick={() => navigate(`/board-reports/${id}/runs/${latest.id}/preview`)}
              >
                Preview
              </button>
            </div>
          </>
        ) : (
          <p className="text-sm text-text-2">
            No runs yet. Generate the first run to produce a board-ready draft you can review and approve.
          </p>
        )}
      </div>

      {/* Delivery */}
      <div className="bg-card border border-card-bd rounded-card p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-sm font-semibold text-text">Delivery</h3>
            <p className="mt-1 text-sm text-text-2">Who receives the report and when it&apos;s sent.</p>
          </div>
          <button type="button" className={secondaryButtonCls} onClick={onManageDelivery}>
            Manage
          </button>
        </div>
        {recipients.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {recipients.map((r, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li
                key={`${r.email}-${index}`}
                className="inline-flex items-center px-3 py-1.5 rounded-full bg-background-grey border border-border text-sm text-text"
              >
                {r.name ? `${r.name} <${r.email}>` : r.email}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-text-3">
            No recipients yet — add them in Settings. The report can still be previewed and downloaded without them.
          </p>
        )}
      </div>
    </div>
  );
};

OverviewPanel.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  report: PropTypes.object,
  runs: PropTypes.array,
  isLoading: PropTypes.bool,
  onManageDelivery: PropTypes.func.isRequired,
};

export default OverviewPanel;
